import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardContent } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import moment from "moment";
import { convertSecondToDays, formatBytes } from "../../../utils/common";
import useTrafficReport from "../../../hooks/useTrafficReport";
import AdvanceFilter from "./TrafficReportAdvanceFilter";
import { useDialog } from "../../../contexts/DialogContext";

function TrafficReport({ userName }) {
  const { t } = useTranslation();

  const { openDialog } = useDialog();

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const currentPayload = {
    filters: [
      {
        fieldName: "userName",
        fieldValue: userName,
        op: "eq",
        dataType: "int",
        condition: "and",
        isJoin: true,
        attributeName: "radUser",
        entityName: "RadUserEntity",
      },
      {
        fieldName: "reportType",
        fieldValue: "daily",
        op: "eq",
        dataType: "string",
        condition: "and",
        isJoin: false,
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "id",
        sort: "DESC",
      },
    ],
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);

  const { height } = useWindowDimensions();
  const { getAllTrafficReport, trafficReports, downloadFile, paging } = useTrafficReport();
  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const { width: mainContentWidth } = useMainContentDimensions();

  const [payload, setPayload] = useState(currentPayload);

  const fetchData = async () => {
    await getAllTrafficReport(payload);
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");
    const pagination = {
      pageNumber: newPaginationModel.page == 0 ? 1 : newPaginationModel.page + 1,
      pageSize: payload.pagination.pageSize,
    };
    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "sessionDate",
      headerName: "Date",
      // valueGetter: (params) => moment(params).format("DD-MM-YYYY HH:mm a"),
      width: 200,
    },
    {
      field: "onlineTime",
      headerName: "Online Time",
      valueGetter: (params) => convertSecondToDays(params),
      width: 200,
    },
    {
      field: "upload",
      headerName: "Uploaded",
      valueGetter: (params) => formatBytes(params),
      width: 130,
    },
    {
      field: "download",
      headerName: "Downloaded",
      valueGetter: (params) => formatBytes(params),
      width: 130,
    },
    {
      field: "total",
      headerName: "Total",
      valueGetter: (params) => formatBytes(params),
      width: 150,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        openFilterDialog={openFilterDialog}
        setFilterResetFunc={setFilterResetFunc}
        userName={userName}
      />,
    );
  };

  const exportFile = async (fileType) => {
    await downloadFile(fileType, payload);
  };

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        action={<Toolbar downloadFile={exportFile} openFilterDialog={openFilterDialog} />}
        title={t("traffic_report")}
        // subheader={breadCrump()}

        downloadFile={downloadFile}
      />
      <CardContent sx={{ height: height - 180 }}>
        <DataGrid
          columns={columns}
          rows={trafficReports}
          getRowId={(row) => row["sessionDate"]}
          rowCount={paging.totalNumberOfRecords}
          loading={false}
          rowsPerPageOptions={[5]}
          pagination
          page={paging.pageNumber}
          pageSize={paging.pageSize}
          paginationMode="server"
          onPaginationModelChange={onPageChange}
          checkboxSelection
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationMeta={paginationMeta}
          paging={{
            pageNumber: pageNumber,
            pageSize: pageSize,
          }}
          paginationModel={paginationModel}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
          }
          sx={{
            "& .MuiDataGrid-row.even-row": {
              backgroundColor: "#f9f9f9", // Light gray for even rows
            },
            "& .MuiDataGrid-row.odd-row": {
              backgroundColor: "#ffffff", // White for odd rows
            },
          }}
        />
      </CardContent>
    </Card>
  );
}

export default TrafficReport;
